import type { ReactNode } from "react";
import { Link, type LinkProps } from "@tanstack/react-router";
import {
	Banknote,
	BookOpen,
	CalendarDays,
	LayoutDashboard,
	type LucideIcon,
	ListTodo,
	MessagesSquare,
	LogOut,
	ServerCog,
	Stethoscope,
	Users,
	X,
} from "lucide-react";

import type { MeUser } from "@/api/db/users";
import { OverlayShell } from "@/components/ui/overlay";
import { useLogout } from "@/hooks/use-logout";
import { CLINIC_BRAND } from "@/lib/brand";
import { AuthLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

import { AgoraNavBadge } from "./agora-nav-badge";
import { ClinicaNavBadge } from "./clinica-nav-badge";
import { FinanceiroNavBadge } from "./financeiro-nav-badge";
import { PessoasNavBadge } from "./pessoas-nav-badge";
import { PulsoMark } from "./pulso-mark";

function SidebarLink({
	to,
	exact,
	icon: Icon,
	label,
	trailing,
	onNavigate,
	compact,
}: {
	to: LinkProps["to"];
	exact?: boolean;
	icon: LucideIcon;
	label: string;
	trailing?: ReactNode;
	onNavigate?: () => void;
	compact: boolean;
}) {
	return (
		<Link
			to={to}
			onClick={onNavigate}
			activeOptions={exact ? { exact: true } : undefined}
			className={cn(
				"relative flex min-h-11 items-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-accent",
				compact
					? "min-h-16 flex-col justify-center gap-1 px-1 py-2 text-xs"
					: "gap-3 px-4 py-3 text-sm",
			)}
			activeProps={{ className: "bg-sidebar-accent text-sidebar" }}
			inactiveProps={{
				className: "text-sidebar-muted hover:bg-sidebar-foreground/8 hover:text-sidebar-foreground",
			}}
		>
			<Icon className="size-5 shrink-0" />
			<span>{label}</span>
			{!!trailing && (
				<span className={cn("flex", compact ? "absolute right-1 top-1" : "ml-auto")}>
					{trailing}
				</span>
			)}
		</Link>
	);
}

function SidebarContent({
	user,
	onNavigate,
	compact = false,
}: {
	user: MeUser;
	onNavigate?: () => void;
	compact?: boolean;
}) {
	const { logout } = useLogout();

	return (
		<>
			<div className={cn("flex items-center", compact ? "justify-center py-5" : "gap-3 p-5")}>
				<Link
					to="/"
					onClick={onNavigate}
					aria-label="Pulso, início"
					className="flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-accent"
				>
					<PulsoMark className="size-8 shrink-0 text-sidebar-accent" />
					{!compact && (
						<div className="min-w-0">
							<p className="font-display text-2xl">Pulso</p>
							<p className="text-xs text-sidebar-muted">{CLINIC_BRAND}</p>
						</div>
					)}
				</Link>
				{!compact && (
					<button
						type="button"
						onClick={onNavigate}
						aria-label="Fechar menu"
						className="ml-auto grid size-11 place-items-center rounded-md text-sidebar-muted hover:bg-sidebar-foreground/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-accent"
					>
						<X className="size-5" />
					</button>
				)}
			</div>

			<nav
				aria-label="Navegação principal"
				className={cn("flex flex-col gap-1", compact ? "px-2" : "px-3")}
			>
				<SidebarLink
					compact={compact}
					to="/"
					exact
					icon={ListTodo}
					label="Agora"
					trailing={<AgoraNavBadge />}
					onNavigate={onNavigate}
				/>
				<SidebarLink
					compact={compact}
					to="/conversas"
					icon={MessagesSquare}
					label="Conversas"
					onNavigate={onNavigate}
				/>
				<SidebarLink
					compact={compact}
					to="/pessoas"
					icon={Users}
					label="Pessoas"
					trailing={<PessoasNavBadge />}
					onNavigate={onNavigate}
				/>
				<SidebarLink
					compact={compact}
					to="/agenda"
					icon={CalendarDays}
					label="Agenda"
					onNavigate={onNavigate}
				/>
				<SidebarLink
					compact={compact}
					to="/financeiro"
					icon={Banknote}
					label="Financeiro"
					trailing={<FinanceiroNavBadge />}
					onNavigate={onNavigate}
				/>
				{user.role === "medical" && (
					<SidebarLink
						compact={compact}
						to="/clinica"
						icon={Stethoscope}
						label="Clínica"
						trailing={<ClinicaNavBadge />}
						onNavigate={onNavigate}
					/>
				)}
				<div className="mx-3 my-2 border-t border-sidebar-foreground/15" />
				<SidebarLink
					compact={compact}
					to="/dashboard"
					icon={LayoutDashboard}
					label="Dashboard"
					onNavigate={onNavigate}
				/>
				<SidebarLink
					compact={compact}
					to="/wiki"
					icon={BookOpen}
					label="Wiki"
					onNavigate={onNavigate}
				/>
				{user.user_type === "admin" && (
					<SidebarLink
						compact={compact}
						to="/sistema"
						icon={ServerCog}
						label="Sistema"
						onNavigate={onNavigate}
					/>
				)}
			</nav>

			<div
				className={cn(
					"mt-auto border-t border-sidebar-foreground/15",
					compact ? "mx-2 mt-6 py-3" : "mt-5 p-5",
				)}
			>
				{!compact && (
					<div className="mb-4">
						<p className="text-sm font-semibold">{user.name}</p>
						<p className="text-xs text-sidebar-muted">
							{AuthLabels.userType[user.user_type]} / {AuthLabels.role[user.role]}
						</p>
					</div>
				)}
				<button
					type="button"
					onClick={logout}
					className={cn(
						"flex min-h-11 w-full items-center justify-center gap-2 rounded-md text-xs text-sidebar-muted hover:bg-sidebar-foreground/8 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-accent",
						compact && "flex-col py-2",
					)}
				>
					<LogOut className="size-4" />
					Sair
				</button>
			</div>
		</>
	);
}

export function AppSidebar({ user }: { user: MeUser }) {
	return (
		<aside className="app-rail">
			<SidebarContent user={user} compact />
		</aside>
	);
}

export function AppSidebarModal({
	user,
	open,
	onClose,
}: {
	user: MeUser;
	open: boolean;
	onClose: () => void;
}) {
	if (!open) {
		return null;
	}

	return (
		<OverlayShell
			label="Menu de navegação"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<div className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-y-auto rounded-xl bg-sidebar text-sidebar-foreground shadow-overlay">
				<SidebarContent user={user} onNavigate={onClose} />
			</div>
		</OverlayShell>
	);
}
